// Libs
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { DropzoneProps, DropzoneRef, useDropzone } from 'react-dropzone';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { IBaseFile } from '~/utils/interface/common';
import {
  FILE_UPLOAD_PROP_MIN_FILE,
  FILE_UPLOAD_PROP_MAX_FILE,
  DEFAULT_HEIGHT_UPLOAD_IMAGE,
  MAX_FILE_SIZE,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseUploadDocument.module.scss';
import { icons } from '~/assets';

export type BaseUploadDocumentProps = DropzoneProps &
  React.RefAttributes<DropzoneRef> & {
    label?: string;
    required?: boolean;
    uploadedFiles?: IBaseFile[];
    isMultiple?: boolean;
    errorMessage?: string;
    width?: number | string;
    height?: number | string;
    onChange?: (files: IBaseFile[]) => void;
    onRemove?: (file: IBaseFile) => void;
  };

const cx = classNames.bind(styles);

const BaseUploadDocument = (props: BaseUploadDocumentProps) => {
  //#region Destructuring Props
  const {
    label,
    required = false,
    isMultiple = true,
    maxFiles = isMultiple ? FILE_UPLOAD_PROP_MAX_FILE : FILE_UPLOAD_PROP_MIN_FILE,
    errorMessage,
    width,
    height = DEFAULT_HEIGHT_UPLOAD_IMAGE,
    onRemove,
    ...restProps
  } = props;
  delete restProps.onChange;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    maxSize: MAX_FILE_SIZE,
    multiple: isMultiple,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
      'text/plain': [],
    },
    ...restProps,
    onDrop: (acceptedFiles: Array<File>) => {
      const updatedFiles = [
        ...uploadFiles,
        ...acceptedFiles.map((item) => ({
          file: item,
          name: item.name,
          url: URL.createObjectURL(item),
        })),
      ];
      setUploadFiles(updatedFiles);

      if (props.onChange) {
        props.onChange(updatedFiles);
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [uploadFiles, setUploadFiles] = useState<IBaseFile[]>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props?.uploadedFiles?.length) {
      setUploadFiles([...props.uploadedFiles.filter((uploadedFile) => !!uploadedFile?.url)]);
    }
  }, [props?.uploadedFiles]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleRemoveFile = (file: IBaseFile, fileIndex: number) => {
    const updatedFiles = uploadFiles.filter((_, index) => index !== fileIndex);
    setUploadFiles(updatedFiles);
    onRemove?.(file);

    if (props.onChange) {
      props.onChange(updatedFiles);
    }
  };

  const renderPreviewFileUpload = (file: IBaseFile, index: number) => {
    return (
      <Stack className={cx('fileItem')}>
        <Stack direction='row' gap={4} className={cx('fileInfo')}>
          <img src={icons.commonIconFile} alt={t('common_alt_img')} />

          <Typography className={cx('fileName')}>{file?.file?.name}</Typography>
        </Stack>

        <IconButton onClick={() => handleRemoveFile(file, index)}>
          <img src={icons.commonIconTrashGrey} alt={t('common_alt_img')} />
        </IconButton>
      </Stack>
    );
  };
  //#endregion Handle Function

  return (
    <Stack gap={2}>
      {label && <BaseLabel label={label} required={required} />}

      {uploadFiles.length < maxFiles && (
        <>
          <Box className={cx('container')} {...getRootProps()} sx={{ width, height }}>
            <input {...getInputProps()} className={cx('inputFile')} />

            <img src={icons.commonIconUploadFileDocument} alt={t('common_alt_img')} className={cx('icon')} />

            <Typography className={cx('description')}>{t('common_upload_document_title')}</Typography>

            <Typography className={cx('description')}>{t('common_upload_document_description')}</Typography>

            <Button type='button' variant='contained' className={cx('buttonChoose')}>
              {t(maxFiles === 1 ? 'common_button_choose_file' : 'common_button_choose_files', { ns: 'common/base' })}
            </Button>
          </Box>

          <Typography className={cx('errorMessage')}>{errorMessage}</Typography>
        </>
      )}

      {uploadFiles?.length > 0 && (
        <Stack spacing={2}>
          {uploadFiles.map((file, index) => (
            <Stack key={index}>{renderPreviewFileUpload(file, index)}</Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
export default memo(BaseUploadDocument);
